import { defineStore } from 'pinia'
import apiService from '../services/api'

export const useMemorialStore = defineStore('memorial', {
  state: () => {
    // Load auth from storage
    const stored = localStorage.getItem('memorial_auth')
    let authData = {
      apiToken: '',
      user: null,
      isEmailVerified: false,
      isAccountCreated: false,
      userEmail: '',
      memorialUuid: null
    }
    
    if (stored) {
      try {
        const data = JSON.parse(stored)
        authData = {
          apiToken: data.token || '',
          user: data.user || null,
          isEmailVerified: data.isEmailVerified || false,
          isAccountCreated: data.isAccountCreated || false,
          userEmail: data.userEmail || '',
          memorialUuid: data.memorialUuid || null,
          memorialData: data.memorialData || null
        }
      } catch (error) {
        console.error('Failed to load auth from storage:', error)
        localStorage.removeItem('memorial_auth')
      }
    }
    
    // Sync apiService token from store on startup to prevent desync
    if (authData.apiToken) {
      apiService.setToken(authData.apiToken)
    }

    // Initialize memorial data from storage if available
    const memorialData = authData.memorialData
    
    return {
    // Form data
    personName: memorialData?.nameOfPerson || '',
    birthDate: memorialData?.birthDate || '',
    deathDate: memorialData?.deathDate || '',
    hasEvent: memorialData?.hasEvent ?? false,
    eventName: memorialData?.eventName || '',
    memorialDate: '',
    memorialHour: null,
    memorialMinute: null,
    memorialAddress: memorialData?.address || '',
    memorialDetails: memorialData?.details || '',
    photos: memorialData?.media ? memorialData.media.filter(media => media.mediaType === 0 || media.mediaType == null).map(media => ({
      id: media.uuid,
      name: media.filename + '.' + media.extension,
      url: media.location ? `${media.location}/${media.filename}.${media.extension}` : `https://assets.passingon.me/photos/${media.filename}.${media.extension}`,
      caption: media.title || '',
      serverData: media
    })) : [],
    audios: memorialData?.media ? memorialData.media.filter(media => media.mediaType === 4).map(media => ({
      id: media.uuid,
      name: media.filename + '.' + media.extension,
      url: media.location ? `${media.location}/${media.filename}.${media.extension}` : `https://assets.passingon.me/photos/${media.filename}.${media.extension}`,
      caption: media.title || '',
      serverData: media
    })) : [],
    stories: memorialData?.stories ? memorialData.stories.map(s => s.text || s) : [''],
    personalMessage: memorialData?.hostMessage || '',
    videoMessage: memorialData?.hostVideoMessage ? {
      uuid: memorialData.hostVideoMessage.uuid,
      filename: memorialData.hostVideoMessage.filename + '.' + memorialData.hostVideoMessage.extension,
      url: memorialData.hostVideoMessage.location ? `${memorialData.hostVideoMessage.location}/${memorialData.hostVideoMessage.filename}.${memorialData.hostVideoMessage.extension}` : `https://assets.passingon.me/photos/${memorialData.hostVideoMessage.filename}.${memorialData.hostVideoMessage.extension}`,
      isServerVideo: true
    } : null,
    profilePhoto: memorialData?.profilePhoto ? {
      uuid: memorialData.profilePhoto.uuid,
      filename: memorialData.profilePhoto.filename + '.' + memorialData.profilePhoto.extension,
      url: memorialData.profilePhoto.location ? `${memorialData.profilePhoto.location}/${memorialData.profilePhoto.filename}.${memorialData.profilePhoto.extension}` : `https://assets.passingon.me/photos/${memorialData.profilePhoto.filename}.${memorialData.profilePhoto.extension}`,
      title: memorialData.profilePhoto.title || ''
    } : null,
    profilePhotoFile: null,
    contacts: memorialData?.contacts ? memorialData.contacts.map(c => c.email || c.phone || c) : [],
    userEmail: authData.userEmail,
    userPassword: '',
    isEmailVerified: authData.isEmailVerified,
    isAccountCreated: authData.isAccountCreated,
    verificationCode: '',
    paymentCompleted: memorialData?.paymentCompleted || false,
    paymentCompletedAt: memorialData?.paymentCompletedAt || null,
    stripeCustomerId: memorialData?.stripeCustomerId || null,
    promoApplied: memorialData?.promoApplied || null,
    shareSlug: memorialData?.shareSlug || null,
    shareSlugPendingPersist: false,

    // API authentication
    apiToken: authData.apiToken,
    user: authData.user, // Will store user object from API
    memorialUuid: authData.memorialUuid, // UUID of created memorial
    memorialVersion: memorialData?.version ?? null,

    // UI state
    currentStep: 0,
    maxReachedStep: 0,
    steps: ['Welcome', 'Who', 'Photos', 'Message', 'Preview', 'Save', 'Payment', 'Contacts', 'Share'],
    
    // Change tracking
    originalMemorialData: null, // Snapshot of memorial data when loaded from server
    hasUnsavedChanges: false,
    
    // Navigation modal state
    showUnsavedChangesModal: false,
    pendingNavigation: null,
    navigationResolver: null,
    
    // Page-specific change tracking
    pageChanges: {
      who: false,
      photos: false, 
      message: false,
      contacts: false,
      preview: false
    },
    currentPageSnapshot: null,
    currentPageName: null
    }
  },
  
  getters: {
    // Real-time check for changes
    hasUnsavedChangesComputed() {
      if (!this.originalMemorialData || !this.isAccountCreated || !this.isEmailVerified) {
        return false
      }

      // Compare current data with original
      if (this.personName !== this.originalMemorialData.personName ||
          this.birthDate !== this.originalMemorialData.birthDate ||
          this.deathDate !== this.originalMemorialData.deathDate ||
          this.hasEvent !== this.originalMemorialData.hasEvent ||
          this.eventName !== this.originalMemorialData.eventName ||
          this.memorialAddress !== this.originalMemorialData.memorialAddress ||
          this.memorialDetails !== this.originalMemorialData.memorialDetails ||
          this.personalMessage !== this.originalMemorialData.personalMessage) {
        return true
      }

      // Compare arrays
      if (this.photos.length !== this.originalMemorialData.photos.length ||
          this.audios.length !== this.originalMemorialData.audios.length ||
          this.stories.length !== this.originalMemorialData.stories.length ||
          this.contacts.length !== this.originalMemorialData.contacts.length) {
        return true
      }

      // Compare array contents
      for (let i = 0; i < this.stories.length; i++) {
        if (this.stories[i] !== this.originalMemorialData.stories[i]) {
          return true
        }
      }

      for (let i = 0; i < this.contacts.length; i++) {
        if (this.contacts[i] !== this.originalMemorialData.contacts[i]) {
          return true
        }
      }

      // For photos, compare file names/IDs if available
      for (let i = 0; i < this.photos.length; i++) {
        const currentPhoto = this.photos[i]
        const originalPhoto = this.originalMemorialData.photos[i]
        if (!currentPhoto || !originalPhoto || 
            currentPhoto.id !== originalPhoto.id || 
            currentPhoto.name !== originalPhoto.name) {
          return true
        }
      }

      // Compare video message
      if (this.videoMessage !== this.originalMemorialData.videoMessage) {
        return true
      }

      return false
    },

    // Check if first-time users have data worth protecting
    hasDataToProtect() {
      // Only apply to first-time users (not logged in/verified)
      if (this.isAccountCreated && this.isEmailVerified) {
        return false
      }

      // Check if user has entered any meaningful data
      return !!(
        this.personName?.trim() ||
        this.birthDate?.trim() ||
        this.deathDate?.trim() ||
        this.eventName?.trim() ||
        this.memorialAddress?.trim() ||
        this.memorialDetails?.trim() ||
        this.personalMessage?.trim() ||
        this.photos.length > 0 ||
        this.audios.length > 0 ||
        this.videoMessage ||
        this.stories.some(story => story?.trim()) ||
        this.contacts.some(contact => contact?.trim())
      )
    }
  },
  
  actions: {
    updatePersonName(name) {
      this.personName = name
      this.markAsChanged()
    },
    
    updateBirthDate(birthDate) {
      this.birthDate = birthDate
      this.markAsChanged()
    },

    updateDeathDate(deathDate) {
      this.deathDate = deathDate
      this.markAsChanged()
    },

    updateHasEvent(hasEvent) {
      this.hasEvent = hasEvent
      this.markAsChanged()
    },

    updateEventName(eventName) {
      this.eventName = eventName
      this.markAsChanged()
    },


    updateMemorialDate(date) {
      this.memorialDate = date
    },

    updateMemorialHour(hour) {
      this.memorialHour = hour
    },

    updateMemorialMinute(minute) {
      this.memorialMinute = minute
    },

    updateMemorialAddress(address) {
      this.memorialAddress = address
      this.markAsChanged()
    },
    
    updateMemorialDetails(details) {
      this.memorialDetails = details
      this.markAsChanged()
    },

    updateProfilePhoto(file) {
      this.profilePhotoFile = file
      this.markAsChanged()
    },

    addPhoto(photo) {
      this.photos.push(photo)
      this.markAsChanged()
    },
    
    removePhoto(index) {
      this.photos.splice(index, 1)
      this.markAsChanged()
    },
    
    updatePhotoCaption(index, caption) {
      if (this.photos[index]) {
        this.photos[index].caption = caption
        // Update serverData title as well if it exists (for tracking changes)
        if (this.photos[index].serverData) {
          // Keep original title in serverData for change detection, but don't modify it
          // The change detection will compare serverData.title with current caption
        }
        this.markAsChanged()
      }
    },

    updateAudios(audios) {
      this.audios = [...audios]
      this.markAsChanged()
    },
    
    updateStory(index, story) {
      this.stories[index] = story
      this.markAsChanged()
    },
    
    addStory() {
      this.stories.push('')
      this.markAsChanged()
    },
    
    removeStory(index) {
      if (this.stories.length > 1) {
        this.stories.splice(index, 1)
        this.markAsChanged()
      }
    },
    
    updatePersonalMessage(message) {
      this.personalMessage = message
      this.markAsChanged()
    },
    
    updateVideoMessage(video) {
      this.videoMessage = video
      this.markAsChanged()
    },
    
    updateContacts(contactList) {
      this.contacts = [...contactList]
      this.markAsChanged()
    },
    
    addContact(value) {
      this.contacts.push(value)
      this.markAsChanged()
    },
    
    removeContact(index) {
      this.contacts.splice(index, 1)
      this.markAsChanged()
    },
    
    updateUserEmail(email) {
      this.userEmail = email
    },
    
    updateUserPassword(password) {
      this.userPassword = password
    },

    sanitizeShareSlugPart(value) {
      if (!value) return ''
      return value.toString().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+/, '').replace(/-+$/, '')
    },

    generateShareSlug() {
      console.log('generateShareSlug');
      return null;
      // const base = this.sanitizeShareSlugPart(this.personName) || 'memorial'
      // const source = (this.memorialUuid || Date.now().toString(36)).toString().replace(/[^a-z0-9]/gi, '') || Date.now().toString(36)
      // const uniquePart = source.toLowerCase().slice(-6)
      // return `${base}-${uniquePart || '000000'}`
    },

    persistShareSlugToStorage() {
      const stored = localStorage.getItem('memorial_auth')
      let data
      if (stored) {
        try {
          data = JSON.parse(stored)
        } catch (error) {
          console.error('Failed to parse memorial_auth while saving share slug:', error)
          data = null
        }
      }

      if (!data) {
        data = {
          token: this.apiToken,
          user: this.user,
          isAccountCreated: this.isAccountCreated,
          isEmailVerified: this.isEmailVerified,
          userEmail: this.userEmail,
          memorialUuid: this.memorialUuid,
          memorialData: {}
        }
      }

      data.memorialData = data.memorialData || {}
      data.memorialData.shareSlug = this.shareSlug
      localStorage.setItem('memorial_auth', JSON.stringify(data))
    },

    ensureShareSlug() {
      // MB commented out 27 Feb 2026 to keep slug generation on the server side, the client may cause a duplicate that it is unaware of, the server side can check
      // if (!this.shareSlug) {
      //   this.shareSlug = this.generateShareSlug()
      //   this.shareSlugPendingPersist = true
      //   this.persistShareSlugToStorage()
      // }
      return this.shareSlug
    },

    setShareSlug(slug) {
      if (!slug) return
      this.shareSlug = slug
      this.shareSlugPendingPersist = false
      this.persistShareSlugToStorage()
    },

    markShareSlugPersisted() {
      this.shareSlugPendingPersist = false
      this.persistShareSlugToStorage()
    },

    verifyEmail(code) {
      // In a real app, this would validate against the server
      this.verificationCode = code
      this.isEmailVerified = true
    },
    
    markAccountCreated() {
      this.isAccountCreated = true
    },
    
    setApiAuth(token, user) {
      this.apiToken = token
      this.user = user
      this.isAccountCreated = true
      this.isEmailVerified = true
      if (user.email) {
        this.userEmail = user.email
      }
      // Keep apiService in sync
      apiService.setToken(token)
      
      // Persist to localStorage
      localStorage.setItem('memorial_auth', JSON.stringify({
        token,
        user,
        isAccountCreated: true,
        isEmailVerified: true,
        userEmail: user.email,
        memorialUuid: this.memorialUuid,
        memorialData: {
          version: this.memorialVersion,
          nameOfPerson: this.personName,
          birthDate: this.birthDate,
          deathDate: this.deathDate,
          hasEvent: this.hasEvent,
          eventName: this.eventName,
          address: this.memorialAddress,
          details: this.memorialDetails,
        stories: this.stories,
        contacts: this.contacts,
        hostMessage: this.personalMessage,
        paymentCompleted: this.paymentCompleted,
        paymentCompletedAt: this.paymentCompletedAt,
        stripeCustomerId: this.stripeCustomerId,
        shareSlug: this.shareSlug
        }
      }))
    },
    
    clearApiAuth() {
      this.apiToken = ''
      this.user = null
      this.isAccountCreated = false
      this.isEmailVerified = false
      this.memorialUuid = null
      localStorage.removeItem('memorial_auth')
      // Keep apiService in sync
      apiService.clearToken()
    },
    
    setMemorialUuid(uuid) {
      this.memorialUuid = uuid
      // Persist to localStorage
      const stored = localStorage.getItem('memorial_auth')
      if (stored) {
        try {
          const data = JSON.parse(stored)
          data.memorialUuid = uuid
          localStorage.setItem('memorial_auth', JSON.stringify(data))
        } catch (error) {
          console.error('Failed to persist memorial UUID:', error)
        }
      }
    },

    setMemorialVersion(version) {
      if (version == null) return

      this.memorialVersion = version

      const stored = localStorage.getItem('memorial_auth')
      if (!stored) return

      try {
        const data = JSON.parse(stored)
        const storedUuid = data.memorialUuid || data.memorialData?.uuid
        if (storedUuid !== this.memorialUuid) return

        data.memorialData = data.memorialData || {}
        data.memorialData.uuid = this.memorialUuid
        data.memorialData.version = version
        localStorage.setItem('memorial_auth', JSON.stringify(data))
      } catch (error) {
        console.error('Failed to persist memorial version:', error)
      }
    },
    
    loadAuthFromStorage() {
      const stored = localStorage.getItem('memorial_auth')
      if (stored) {
        try {
          const data = JSON.parse(stored)
          this.apiToken = data.token
          this.user = data.user
          this.isAccountCreated = data.isAccountCreated
          this.isEmailVerified = data.isEmailVerified
          this.userEmail = data.userEmail
          this.memorialUuid = data.memorialUuid || null
          
          // Load memorial data if available
          if (data.memorialData) {
            this.personName = data.memorialData.nameOfPerson || ''
            this.birthDate = data.memorialData.birthDate || ''
            this.deathDate = data.memorialData.deathDate || ''
            this.hasEvent = data.memorialData.hasEvent ?? false
            this.eventName = data.memorialData.eventName || ''
            this.memorialAddress = data.memorialData.address || ''
            this.memorialDetails = data.memorialData.details || ''
            this.personalMessage = data.memorialData.hostMessage || ''
            this.paymentCompleted = data.memorialData.paymentCompleted || false
            this.paymentCompletedAt = data.memorialData.paymentCompletedAt || null
            this.stripeCustomerId = data.memorialData.stripeCustomerId || null
            this.shareSlug = data.memorialData.shareSlug || null
            this.memorialVersion = data.memorialData.version ?? null
            
            if (data.memorialData.stories) {
              this.stories = data.memorialData.stories.map(s => s.text || s)
            }
            
            if (data.memorialData.contacts) {
              this.contacts = data.memorialData.contacts.map(c => c.email || c.phone || c)
            }

            // If user is logged in and has memorial data, set baseline for change tracking
            if (this.isAccountCreated && this.isEmailVerified && this.apiToken) {
              this.setOriginalMemorialData()

              // Load memorial-specific maxReachedStep
              if (data.memorialData.maxReachedStep !== undefined && data.memorialData.maxReachedStep !== null) {
                this.maxReachedStep = data.memorialData.maxReachedStep
              } else if (data.memorialData.paymentCompleted) {
                // If payment completed but no maxReachedStep tracked, allow full navigation
                this.maxReachedStep = 8
              } else {
                // Legacy: default to Payment step for existing memorials
                this.maxReachedStep = 7
              }
            }
          }
        } catch (error) {
          console.error('Failed to load auth from storage:', error)
          localStorage.removeItem('memorial_auth')
        }
      }
    },
    

    completePayment(memorial = null) {
      if (memorial && typeof memorial.paymentCompleted === 'boolean') {
        this.paymentCompleted = memorial.paymentCompleted
      } else {
        this.paymentCompleted = true
      }

      if (memorial) {
        if (typeof memorial.paymentCompletedAt !== 'undefined' && memorial.paymentCompletedAt !== null) {
          this.paymentCompletedAt = memorial.paymentCompletedAt
        } else if (!this.paymentCompletedAt) {
          this.paymentCompletedAt = Date.now()
        }
        if (memorial.stripeCustomerId) {
          this.stripeCustomerId = memorial.stripeCustomerId
        }
      } else if (!this.paymentCompletedAt) {
        this.paymentCompletedAt = Date.now()
      }

      const stored = localStorage.getItem('memorial_auth')
      if (stored) {
        try {
          const data = JSON.parse(stored)
          data.memorialData = data.memorialData || {}
          data.memorialData.paymentCompleted = true
          data.memorialData.paymentCompletedAt = this.paymentCompletedAt
          if (this.stripeCustomerId) {
            data.memorialData.stripeCustomerId = this.stripeCustomerId
          }
          localStorage.setItem('memorial_auth', JSON.stringify(data))
        } catch (error) {
          console.error('Failed to persist payment status:', error)
        }
      }
    },
    
    nextStep() {
      if (this.currentStep < this.steps.length - 1) {
        this.currentStep++
      }
    },
    
    previousStep() {
      if (this.currentStep > 0) {
        this.currentStep--
      }
    },
    
    goToStep(step) {
      this.currentStep = step
      // Update max reached step if we're moving forward
      if (step > this.maxReachedStep) {
        this.maxReachedStep = step
        // Persist the updated maxReachedStep for this memorial
        this.persistMaxReachedStep()
      }
    },

    async persistMaxReachedStep() {
      // Only persist if we have a memorial UUID and are logged in
      if (!this.memorialUuid || !this.apiToken) {
        return
      }

      // Update localStorage immediately
      const stored = localStorage.getItem('memorial_auth')
      if (stored) {
        try {
          const data = JSON.parse(stored)
          if (data.memorialData) {
            data.memorialData.maxReachedStep = this.maxReachedStep
            localStorage.setItem('memorial_auth', JSON.stringify(data))
          }
        } catch (error) {
          console.error('Failed to persist maxReachedStep to localStorage:', error)
        }
      }

      // Also send to backend to persist on server
      try {
        const apiService = (await import('../services/api')).default
        await apiService.post(`/memorial/progress/${this.memorialUuid}`, {
          maxReachedStep: this.maxReachedStep
        })
      } catch (error) {
        console.error('Failed to persist maxReachedStep to server:', error)
        // Don't throw - this is a non-critical update
      }
    },

    loadMemorialData(memorial) {
      // Load memorial data into the store
      this.memorialVersion = memorial.version ?? null
      this.personName = memorial.nameOfPerson || ''
      this.birthDate = memorial.birthDate || ''
      this.deathDate = memorial.deathDate || ''
      this.hasEvent = memorial.hasEvent ?? false
      this.eventName = memorial.eventName || ''
      this.memorialDate = memorial.memorialDate || ''
      this.memorialHour = memorial.memorialHour ?? null
      this.memorialMinute = memorial.memorialMinute ?? null
      this.memorialAddress = memorial.address || ''
      this.memorialDetails = memorial.details || ''
      this.memorialUuid = memorial.uuid
      this.personalMessage = memorial.hostMessage || ''

      // Load media/photos from server
      if (memorial.media && Array.isArray(memorial.media)) {
        this.photos = memorial.media.filter(media => media.mediaType === 0 || media.mediaType == null).map(media => ({
          id: media.uuid,
          name: media.filename + '.' + media.extension,
          url: media.location ? `${media.location}/${media.filename}.${media.extension}` : `https://assets.passingon.me/photos/${media.filename}.${media.extension}`,
          caption: media.title || '',
          serverData: media // Store original server data
        }))
        this.audios = memorial.media.filter(media => media.mediaType === 4).map(media => ({
          id: media.uuid,
          name: media.filename + '.' + media.extension,
          url: media.location ? `${media.location}/${media.filename}.${media.extension}` : `https://assets.passingon.me/photos/${media.filename}.${media.extension}`,
          caption: media.title || '',
          serverData: media
        }))
      } else {
        this.photos = []
        this.audios = []
      }

      // Load host video message
      if (memorial.hostVideoMessage) {
        // Store video info but can't recreate the actual File object
        this.videoMessage = {
          uuid: memorial.hostVideoMessage.uuid,
          filename: memorial.hostVideoMessage.filename + '.' + memorial.hostVideoMessage.extension,
          url: memorial.hostVideoMessage.location ? `${memorial.hostVideoMessage.location}/${memorial.hostVideoMessage.filename}.${memorial.hostVideoMessage.extension}` : `https://assets.passingon.me/photos/${memorial.hostVideoMessage.filename}.${memorial.hostVideoMessage.extension}`,
          isServerVideo: true
        }
      }

      // Load profile photo
      if (memorial.profilePhoto) {
        this.profilePhoto = {
          uuid: memorial.profilePhoto.uuid,
          filename: memorial.profilePhoto.filename + '.' + memorial.profilePhoto.extension,
          url: memorial.profilePhoto.location ? `${memorial.profilePhoto.location}/${memorial.profilePhoto.filename}.${memorial.profilePhoto.extension}` : `https://assets.passingon.me/photos/${memorial.profilePhoto.filename}.${memorial.profilePhoto.extension}`,
          title: memorial.profilePhoto.title || ''
        }
      } else {
        this.profilePhoto = null
      }

      // Load stories
      if (memorial.stories && Array.isArray(memorial.stories)) {
        console.log('Loading stories from memorial:', memorial.stories)
        this.stories = memorial.stories.map(story => story.text || story)
        console.log('Processed stories:', this.stories)
        if (this.stories.length === 0) {
          this.stories = [''] // Ensure at least one empty story
        }
      }

      // Load contacts
      if (memorial.contacts && Array.isArray(memorial.contacts)) {
        console.log('loadMemorialData - loading contacts from memorial:', memorial.contacts)
        this.contacts = memorial.contacts.map(contact => {
          if (contact.email) return contact.email
          if (contact.phone) return contact.phone
          return contact
        })
        console.log('loadMemorialData - contacts loaded into store:', this.contacts)
      } else {
        console.log('loadMemorialData - NO contacts in memorial data')
      }

      // Set payment status from explicit flag with fallback to legacy stripeCustomerId
      const paymentStatus = typeof memorial.paymentCompleted === 'boolean'
        ? memorial.paymentCompleted
        : Boolean(memorial.stripeCustomerId)

      this.stripeCustomerId = memorial.stripeCustomerId || null
      this.promoApplied = memorial.promoApplied || null
      this.shareSlug = memorial.shareSlug || this.shareSlug
      this.paymentCompleted = paymentStatus
      this.paymentCompletedAt = memorial.paymentCompletedAt || (paymentStatus ? this.paymentCompletedAt || Date.now() : null)
      this.shareSlugPendingPersist = !this.shareSlug

      // Mark account as created since user is logged in
      this.isAccountCreated = true
      this.isEmailVerified = true

      // Load memorial's step progression
      // If memorial has been paid for, allow full navigation
      // Otherwise, respect the memorial's max reached step
      if (paymentStatus) {
        this.maxReachedStep = 8 // Allow navigation to all steps including Share
      } else if (memorial.maxReachedStep !== undefined && memorial.maxReachedStep !== null) {
        this.maxReachedStep = memorial.maxReachedStep
      } else {
        // Default to Payment step (7) for existing memorials without maxReachedStep tracked
        this.maxReachedStep = 7
      }

      // Set baseline for change tracking
      this.setOriginalMemorialData()

      // Persist memorial data
      this.persistMemorialData(memorial)
    },

    persistMemorialData(memorial) {
      const memorialData = {
        uuid: memorial.uuid,
        version: memorial.version ?? this.memorialVersion,
        nameOfPerson: memorial.nameOfPerson,
        birthDate: memorial.birthDate ?? this.birthDate,
        deathDate: memorial.deathDate ?? this.deathDate,
        hasEvent: memorial.hasEvent ?? this.hasEvent,
        eventName: memorial.eventName ?? this.eventName,
        address: memorial.address,
        details: memorial.details,
        stories: memorial.stories || this.stories.filter(s => s && s.trim()).map(s => ({ text: s })),
        contacts: memorial.contacts,
        hostMessage: memorial.hostMessage,
        stripeCustomerId: memorial.stripeCustomerId,
        paymentCompleted: typeof memorial.paymentCompleted === 'boolean' ? memorial.paymentCompleted : this.paymentCompleted,
        paymentCompletedAt: memorial.paymentCompletedAt || this.paymentCompletedAt,
        shareSlug: memorial.shareSlug || this.shareSlug,
        maxReachedStep: memorial.maxReachedStep !== undefined ? memorial.maxReachedStep : this.maxReachedStep,
        // Persist media data for page refresh
        media: memorial.media || [
          ...this.photos.map(photo => ({
          uuid: photo.id,
          filename: photo.serverData?.filename || photo.name?.split('.')[0] || 'photo',
          extension: photo.serverData?.extension || photo.name?.split('.').pop() || 'jpg',
          location: photo.url,
          title: photo.caption || '',
          mediaType: photo.serverData?.mediaType || 0
          })),
          ...this.audios.filter(audio => audio.id).map(audio => ({
            uuid: audio.id,
            filename: audio.serverData?.filename || audio.name?.split('.')[0] || 'audio',
            extension: audio.serverData?.extension || audio.name?.split('.').pop() || 'webm',
            location: audio.serverData?.location || null,
            title: audio.caption || '',
            mediaType: 4,
            transcript: audio.serverData?.transcript || null,
            transcriptionStatus: audio.serverData?.transcriptionStatus || null
          }))
        ],
        hostVideoMessage: memorial.hostVideoMessage || (this.videoMessage?.uuid ? {
          uuid: this.videoMessage.uuid,
          filename: this.videoMessage.filename?.split('.')[0] || 'video',
          extension: this.videoMessage.filename?.split('.').pop() || 'webm',
          location: this.videoMessage.url,
          mediaType: 1
        } : null),
        profilePhoto: memorial.profilePhoto || (this.profilePhoto?.uuid ? {
          uuid: this.profilePhoto.uuid,
          filename: this.profilePhoto.filename?.split('.')[0] || 'profile',
          extension: this.profilePhoto.filename?.split('.').pop() || 'jpg',
          location: this.profilePhoto.url?.includes('://') ? this.profilePhoto.url.substring(0, this.profilePhoto.url.lastIndexOf('/')) : null,
          title: this.profilePhoto.title || ''
        } : null)
      }

      // Update localStorage with memorial data and memorialUuid
      const stored = localStorage.getItem('memorial_auth')
      let data

      if (stored) {
        try {
          data = JSON.parse(stored)
        } catch (error) {
          console.error('Failed to parse memorial_auth:', error)
          data = null
        }
      }

      // Create data structure if it doesn't exist
      if (!data) {
        data = {
          token: this.apiToken,
          user: this.user,
          isAccountCreated: this.isAccountCreated,
          isEmailVerified: this.isEmailVerified,
          userEmail: this.userEmail
        }
      }

      // Update with memorial data and UUID
      data.memorialData = memorialData
      data.memorialUuid = memorial.uuid

      try {
        localStorage.setItem('memorial_auth', JSON.stringify(data))
      } catch (error) {
        console.error('Failed to persist memorial data:', error)
      }
    },

    clearMemorialData() {
      // Clear all memorial-specific data
      this.personName = ''
      this.birthDate = ''
      this.deathDate = ''
      this.hasEvent = false
      this.eventName = ''
      this.memorialAddress = ''
      this.memorialDetails = ''
      this.photos = []
      this.audios = []
      this.stories = ['']
      this.personalMessage = ''
      this.videoMessage = null
      this.contacts = []
      this.memorialUuid = null
      this.memorialVersion = null
      this.paymentCompleted = false
      this.paymentCompletedAt = null
      this.stripeCustomerId = null
      this.shareSlug = null
      this.shareSlugPendingPersist = false
      this.originalMemorialData = null
      this.hasUnsavedChanges = false
      // Reset step progression for new memorial
      this.maxReachedStep = 0
      this.currentStep = 0
      // Clear page tracking snapshots
      this.currentPageSnapshot = null
      this.currentPageName = null
      this.pageChanges = {
        who: false,
        photos: false,
        message: false,
        contacts: false,
        preview: false
      }
    },

    clearAllData() {
      // Clear authentication
      this.clearApiAuth()
      // Clear memorial data
      this.clearMemorialData()
      // Clear profile photo
      this.profilePhoto = null
      this.profilePhotoFile = null
      // Clear localStorage
      localStorage.removeItem('memorial_auth')
      localStorage.removeItem('memorial_data')
    },

    // Change tracking methods
    markAsChanged() {
      // Only mark as changed if we have a baseline and user is logged in
      if (this.originalMemorialData !== null && this.isAccountCreated && this.isEmailVerified) {
        this.hasUnsavedChanges = true
      }
    },

    setOriginalMemorialData() {
      // Create a snapshot of current memorial data
      this.originalMemorialData = {
        personName: this.personName,
        birthDate: this.birthDate,
        deathDate: this.deathDate,
        hasEvent: this.hasEvent,
        eventName: this.eventName,
        memorialAddress: this.memorialAddress,
        memorialDetails: this.memorialDetails,
        photos: [...this.photos],
        audios: [...this.audios],
        stories: [...this.stories],
        personalMessage: this.personalMessage,
        videoMessage: this.videoMessage,
        contacts: [...this.contacts]
      }
      this.hasUnsavedChanges = false
    },

    checkForChanges() {
      if (!this.originalMemorialData) {
        return false
      }

      // Compare current data with original
      const current = {
        personName: this.personName,
        birthDate: this.birthDate,
        deathDate: this.deathDate,
        hasEvent: this.hasEvent,
        eventName: this.eventName,
        memorialAddress: this.memorialAddress,
        memorialDetails: this.memorialDetails,
        photos: this.photos,
        audios: this.audios,
        stories: this.stories,
        personalMessage: this.personalMessage,
        videoMessage: this.videoMessage,
        contacts: this.contacts
      }

      // Simple deep comparison for basic fields
      if (current.personName !== this.originalMemorialData.personName ||
          current.birthDate !== this.originalMemorialData.birthDate ||
          current.deathDate !== this.originalMemorialData.deathDate ||
          current.hasEvent !== this.originalMemorialData.hasEvent ||
          current.eventName !== this.originalMemorialData.eventName ||
          current.memorialAddress !== this.originalMemorialData.memorialAddress ||
          current.memorialDetails !== this.originalMemorialData.memorialDetails ||
          current.personalMessage !== this.originalMemorialData.personalMessage ||
          current.videoMessage !== this.originalMemorialData.videoMessage) {
        return true
      }

      // Compare arrays
      if (current.photos.length !== this.originalMemorialData.photos.length ||
          current.audios.length !== this.originalMemorialData.audios.length ||
          current.stories.length !== this.originalMemorialData.stories.length ||
          current.contacts.length !== this.originalMemorialData.contacts.length) {
        return true
      }

      // Compare array contents
      for (let i = 0; i < current.stories.length; i++) {
        if (current.stories[i] !== this.originalMemorialData.stories[i]) {
          return true
        }
      }

      for (let i = 0; i < current.contacts.length; i++) {
        if (current.contacts[i] !== this.originalMemorialData.contacts[i]) {
          return true
        }
      }

      // For photos, compare file names/IDs if available
      for (let i = 0; i < current.photos.length; i++) {
        const currentPhoto = current.photos[i]
        const originalPhoto = this.originalMemorialData.photos[i]
        if (!currentPhoto || !originalPhoto || 
            currentPhoto.id !== originalPhoto.id || 
            currentPhoto.name !== originalPhoto.name) {
          return true
        }
      }

      return false
    },

    resetChanges() {
      if (this.originalMemorialData) {
        // Reset all data to original state
        this.personName = this.originalMemorialData.personName
        this.birthDate = this.originalMemorialData.birthDate
        this.deathDate = this.originalMemorialData.deathDate
        this.hasEvent = this.originalMemorialData.hasEvent
        this.eventName = this.originalMemorialData.eventName
        this.memorialAddress = this.originalMemorialData.memorialAddress
        this.memorialDetails = this.originalMemorialData.memorialDetails
        this.photos = [...this.originalMemorialData.photos]
        this.audios = [...this.originalMemorialData.audios]
        this.stories = [...this.originalMemorialData.stories]
        this.personalMessage = this.originalMemorialData.personalMessage
        this.videoMessage = this.originalMemorialData.videoMessage
        this.contacts = [...this.originalMemorialData.contacts]
        this.hasUnsavedChanges = false
      }
    },

    markChangesSaved() {
      // Update the baseline after successful save
      this.setOriginalMemorialData()
      // Also update page snapshots to reflect saved state
      this.updateCurrentPageSnapshot()
    },

    clearUnsavedChangesFlag() {
      // Clear the unsaved changes flag without reverting data
      // This is used when user chooses to navigate away and lose changes
      this.hasUnsavedChanges = false
    },

    // Navigation modal methods
    showNavigationModal(pendingNav) {
      return new Promise((resolve) => {
        this.pendingNavigation = pendingNav
        this.navigationResolver = resolve
        this.showUnsavedChangesModal = true
      })
    },

    confirmNavigation() {
      this.showUnsavedChangesModal = false
      if (this.navigationResolver) {
        this.navigationResolver(true)
        this.navigationResolver = null
      }
      this.pendingNavigation = null
      this.clearUnsavedChangesFlag()
    },

    cancelNavigation() {
      this.showUnsavedChangesModal = false
      if (this.navigationResolver) {
        this.navigationResolver(false)
        this.navigationResolver = null
      }
      this.pendingNavigation = null
    },

    // Page-specific change tracking methods
    enterPage(pageName) {
      // Store the current page name for tracking
      this.currentPageName = pageName

      // Take a snapshot of relevant data when entering a page
      switch(pageName) {
        case 'who':
          this.currentPageSnapshot = {
            personName: this.personName,
            birthDate: this.birthDate,
            deathDate: this.deathDate,
            hasEvent: this.hasEvent,
            eventName: this.eventName,
            memorialAddress: this.memorialAddress,
            memorialDetails: this.memorialDetails
          }
          break
        case 'photos':
          this.currentPageSnapshot = {
            photos: [...this.photos],
            audios: [...this.audios],
            stories: [...this.stories]
          }
          break
        case 'message':
          this.currentPageSnapshot = {
            personalMessage: this.personalMessage,
            videoMessage: this.videoMessage
          }
          break
        case 'contacts':
          this.currentPageSnapshot = {
            contacts: [...this.contacts]
          }
          break
        // Preview page removed - it's read-only, no change tracking needed
      }
      // Reset the page change flag
      if (this.pageChanges[pageName] !== undefined) {
        this.pageChanges[pageName] = false
      }
    },

    hasPageChanges(pageName) {
      if (!this.currentPageSnapshot) return false

      switch(pageName) {
        case 'who':
          return this.currentPageSnapshot.personName !== this.personName ||
                 this.currentPageSnapshot.birthDate !== this.birthDate ||
                 this.currentPageSnapshot.deathDate !== this.deathDate ||
                 this.currentPageSnapshot.hasEvent !== this.hasEvent ||
                 this.currentPageSnapshot.eventName !== this.eventName ||
                 this.currentPageSnapshot.memorialAddress !== this.memorialAddress ||
                 this.currentPageSnapshot.memorialDetails !== this.memorialDetails
        case 'photos':
          return this.currentPageSnapshot.photos.length !== this.photos.length ||
                 this.currentPageSnapshot.audios.length !== this.audios.length ||
                 this.currentPageSnapshot.stories.length !== this.stories.length ||
                 !this.arraysEqual(this.currentPageSnapshot.stories, this.stories)
        case 'message':
          return this.currentPageSnapshot.personalMessage !== this.personalMessage ||
                 this.currentPageSnapshot.videoMessage !== this.videoMessage
        case 'contacts':
          return this.currentPageSnapshot.contacts.length !== this.contacts.length ||
                 !this.arraysEqual(this.currentPageSnapshot.contacts, this.contacts)
        // Preview page removed - it's read-only, no change tracking needed
        default:
          return false
      }
    },

    arraysEqual(arr1, arr2) {
      if (arr1.length !== arr2.length) return false
      return arr1.every((val, index) => val === arr2[index])
    },

    clearPageSnapshot() {
      this.currentPageSnapshot = null
      // Reset all page change flags
      Object.keys(this.pageChanges).forEach(page => {
        this.pageChanges[page] = false
      })
    },

    updateCurrentPageSnapshot() {
      // Update the current page snapshot to match current data
      // This is called after successful save to reset change tracking
      if (!this.currentPageName) return
      
      // Re-enter the current page to update the snapshot
      this.enterPage(this.currentPageName)
    },

    async loadUserMemorialFromServer() {
      // Load memorial data from server for logged-in users
      if (!this.apiToken || !this.memorialUuid) {
        console.log('No API token or memorial UUID available for loading memorial data')
        return false
      }

      try {
        apiService.setToken(this.apiToken)
        
        const response = await apiService.getMemorial(this.memorialUuid)
        
        if (response.success && response.memorial) {
          this.loadMemorialData(response.memorial)
          return true
        } else {
          console.error('Failed to load memorial data:', response.msg)
          return false
        }
      } catch (error) {
        console.error('Error loading memorial data from server:', error)
        return false
      }
    },

    initializeForLoggedInUser() {
      // Initialize change tracking for logged-in users
      // This should be called after memorial data is loaded
      if (this.isAccountCreated && this.isEmailVerified && this.apiToken) {
        this.setOriginalMemorialData()
        // Note: maxReachedStep is now managed per-memorial, not globally for logged-in users
        return true
      }
      return false
    }
  }
})
